import { CartResponse } from '@models/cart';
import { GiftItemRow, GiftItemType, GiftOrderResultResponse } from '@models/gift';
import { ItemResponse, ItemRow } from '@models/item';
import { CreateOrderRequest, OrderLine } from '@models/order';
import { OrderResponse } from '@models/order/orderLog';
import { ShopResponse } from '@models/shop';

type GTMOptions = {
    gtmId: string;
};

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[];
    }
}

export function installGTM({ gtmId }: GTMOptions) {
    if (!gtmId) return;

    // 동일 컨테이너 중복 설치 방지
    if (document.querySelector(`[data-gtm-id="${gtmId}"]`)) {
        return;
    }

    window.dataLayer = window.dataLayer || [];

    // GTM 초기화 이벤트
    window.dataLayer.push({
        'gtm.start': Date.now(),
        event: 'gtm.js',
    });

    // GTM 스크립트 삽입
    const script = document.createElement('script');

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    script.dataset.gtmId = gtmId;

    document.head.appendChild(script);

    // noscript iframe 삽입
    const noscript = document.createElement('noscript');
    noscript.dataset.gtmId = gtmId;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    document.body.prepend(noscript);
}

export function pushGtmEvent(
    event: Record<string, unknown>,
) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

export function setGtmUser(userId: string) {
    pushGtmEvent({
        user_id: userId,
        event: 'set_user',
    });
}

/** 로그아웃·탈퇴 시 GA 사용자 해제 */
export function clearGtmUser() {
    pushGtmEvent({
        user_id: null,
        event: 'clear_user',
    });
}

export function pushEcommerceEvent(
    event: string,
    ecommerce: Record<string, unknown>,
) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event, ecommerce,
    });
}

interface ItemRowToGtmFormatProps {
    item: ItemRow;
    categoryName: string;
    shop?: ShopResponse
    index: number;
}

export function itemRowToGtmFormat({
    item, categoryName, shop, index,
}: ItemRowToGtmFormatProps) {
    const { id, name, salePrice } = item;

    return {
        item_id: id,
        item_name: name,
        item_category: categoryName,
        price: salePrice,
        quantity: 1,
        discount: 0,
        affiliation: shop?.name,
        location_id: shop?.id,
        index,
    };
}

interface OrderLineToGtmFormatProps {
    orderLine: OrderLine,
    shop?: Pick<ShopResponse, 'id' | 'name'>,
}

export function orderLineToGtmFormat({ orderLine, shop }: OrderLineToGtmFormatProps) {
    const {
        shopItemId, itemName, defaultOptions, personalOptions,
        totalAmount, quantity,
    } = orderLine;
    return {
        item_id: shopItemId,
        item_name: itemName,
        item_variant: defaultOptions
            .map((it) => it.options)
            .flat().map((it) => it.optionName)
            .join(' | '),
        item_option: personalOptions
            .map((it) => it.options)
            .flat().map((it) => it.optionName)
            .join(' | '),
        price: totalAmount,
        quantity,
        location_id: shop?.id,
        affiliation: shop?.name,
    };
}

export interface ItemToGtmFormatProps {
    item: ItemResponse;
    shop: ShopResponse;
}

export function itemToGtmFormat({ item, shop }:ItemToGtmFormatProps) {
    const {
        id, name, salePrice,
    } = item;
    return {
        item_id: id,
        item_name: name,
        price: salePrice,
        quantity: 1,
        affiliation: shop.name,
        location_id: shop.id,
    };
}

interface CartToGtmFormatProps {
    cart: CartResponse;
    shop: ShopResponse;
}

export function cartToGtmFormat({ cart, shop }:CartToGtmFormatProps) {
    const { items } = cart;
    return items.map((item) => {
        const {
            name, salePrice, itemId, qty,
        } = item;
        return {
            item_id: itemId,
            item_name: name,
            price: salePrice,
            quantity: qty,
            affiliation: shop.name,
            location_id: shop.id,
        };
    });
}

interface GiftItemToGtmFormatProps {
    gift: GiftItemRow;
}

export function giftItemToGtmFormat({ gift }: GiftItemToGtmFormatProps) {
    const { id, name, salePrice } = gift;
    return {
        item_id: id,
        item_name: name,
        price: salePrice,
    };
}

interface OrderCheckoutToGtmFormatProps {
    order: CreateOrderRequest;
    currency: string;
}

/** 상품 주문 add_payment_info / purchase 공통 데이터 */
export function orderCheckoutToGtmFormat({ order, currency }: OrderCheckoutToGtmFormatProps) {
    const {
        shopId, shopName, orderLines, discounts = [],
        orderType, orderPath, paymentMethod,
        totalAmount, totalDiscount, totalCouponDiscount, totalPointDiscount,
        paymentAmount, pgAmount, giftAmount, prepaidCardAmount, cashReceipt,
    } = order;
    const shop = { id: shopId, name: shopName };
    const employeeDiscount = discounts
        .filter((it) => it.type === 'EMPLOYEE')
        .reduce((acc, cur) => acc + cur.discountAmount, 0);
    const coupon = discounts
        .filter((it) => !!it.couponId)
        .map((it) => it.couponName || it.couponId)
        .join(' | ');

    return {
        value: paymentAmount,
        currency,
        coupon: coupon || undefined,
        affiliation: shopName,
        location_id: shopId,
        order_type: orderType,
        order_path: orderPath,
        payment_method: paymentMethod,
        total_amount: totalAmount,
        total_discount: totalDiscount,
        coupon_discount: totalCouponDiscount,
        point_discount: totalPointDiscount,
        employee_discount: employeeDiscount,
        gift_amount: giftAmount,
        prepaid_card_amount: prepaidCardAmount,
        pg_amount: pgAmount,
        cash_receipt_type: cashReceipt?.issueType,
        items: orderLines
            .filter((it) => it.status === 'ON_SALE' && it.quantity > 0)
            .map((orderLine) => orderLineToGtmFormat({ orderLine, shop })),
    };
}

interface PurchaseToGtmFormatProps extends OrderCheckoutToGtmFormatProps {
    orderNo: string;
}

export function purchaseToGtmFormat({ orderNo, order, currency }: PurchaseToGtmFormatProps) {
    return {
        transaction_id: orderNo,
        purchase_type: 'ORDER',
        ...orderCheckoutToGtmFormat({ order, currency }),
    };
}

interface GiftCheckoutToGtmFormatProps {
    gift: GiftItemRow;
    qty: number;
    recipientCount: number;
    currency: string;
}

/** 선물하기(상품·선불카드) begin_checkout / purchase 공통 데이터 */
export function giftCheckoutToGtmFormat({
    gift, qty, recipientCount, currency,
}: GiftCheckoutToGtmFormatProps) {
    const quantity = qty * Math.max(recipientCount, 1);
    return {
        value: gift.salePrice * quantity,
        currency,
        checkout_source: gift.giftItemType === GiftItemType.CASH ? 'PREPAID_CARD_GIFT' : 'GIFT',
        recipient_count: recipientCount,
        items: [{
            ...giftItemToGtmFormat({ gift }),
            item_category: gift.giftItemType,
            quantity,
        }],
    };
}

export type PrepaidCardCheckoutSource = 'PREPAID_CARD_PURCHASE' | 'PREPAID_CARD_CHARGE';

interface PrepaidCardCheckoutToGtmFormatProps {
    id: string;
    name: string;
    amount?: number;
    currency: string;
    source: PrepaidCardCheckoutSource;
}

/** 선불카드 구매·충전 begin_checkout / purchase 공통 데이터 */
export function prepaidCardCheckoutToGtmFormat({
    id, name, amount, currency, source,
}: PrepaidCardCheckoutToGtmFormatProps) {
    return {
        value: amount,
        currency,
        checkout_source: source,
        items: [{
            item_id: id,
            item_name: name,
            item_category: source,
            price: amount,
            quantity: 1,
        }],
    };
}

interface OrderRefundToGtmFormatProps {
    orderNo: string;
    order?: OrderResponse;
    currency: string;
}

/** 상품 주문 취소 refund 데이터 (order 를 못 불러온 경우 transaction_id 만 전송) */
export function orderRefundToGtmFormat({ orderNo, order, currency }: OrderRefundToGtmFormatProps) {
    if (!order) {
        return { transaction_id: orderNo, purchase_type: 'ORDER', currency };
    }
    const {
        shopId, shopName, paymentAmount, paymentMethod, orderItems,
    } = order;
    return {
        transaction_id: orderNo,
        purchase_type: 'ORDER',
        value: paymentAmount,
        currency,
        affiliation: shopName,
        location_id: shopId,
        payment_method: paymentMethod,
        items: orderItems.map((item) => ({
            item_id: item.itemId,
            item_name: item.name,
            price: item.salePrice,
            quantity: item.qty,
            affiliation: shopName,
            location_id: shopId,
        })),
    };
}

interface GiftRefundToGtmFormatProps {
    giftOrderNo: string;
    order?: GiftOrderResultResponse;
    currency: string;
}

/** 선물 결제 취소 refund 데이터 */
export function giftRefundToGtmFormat({ giftOrderNo, order, currency }: GiftRefundToGtmFormatProps) {
    if (!order) {
        return { transaction_id: giftOrderNo, currency };
    }
    const { amount, giftItem, count } = order;
    return {
        transaction_id: giftOrderNo,
        purchase_type: giftItem.giftItemType === GiftItemType.CASH ? 'PREPAID_CARD_GIFT' : 'GIFT',
        value: amount,
        currency,
        items: [{
            item_name: giftItem.name,
            item_category: giftItem.giftItemType,
            price: giftItem.basePrice,
            quantity: count,
        }],
    };
}
